import React from "react";
import { Table, Tag, Collapse, Row, Col } from "antd";
import { IUserRecord, IAccess } from "@/ts/common-types";
import { titleCase } from "@/ts/utils";
import { useMemo } from "react";
import { useState, useEffect } from "react";
import { getUserRoles } from "@/ts/retrieve-data";

interface UserAccessProps {
    user: IUserRecord;
}

const { Panel } = Collapse;

const UserAccess: React.FC<UserAccessProps> = ({ user }) => {
    
    const [availableRoles, setAvailableRoles] = useState([]);
    useEffect(() => {
        getUserRoles().then((data: any) => {
            setAvailableRoles(data);
        });
    }, []);

    const accessColumns = [
        {
            title: "Client ID",
            dataIndex: "client_id",
            key: "client_id",
            width: 100,
        },
        {
            title: "Client Name",
            dataIndex: "client_name",
            key: "client_name",
            ellipsis: true,
        },
    ];

    const roleIdToNameMap = useMemo(() => {
        const map = new Map<number, string>();
        availableRoles?.forEach((role) => {
            map.set(role.role_id, role.role_name);
        });
        return map;
    }, [availableRoles]);

    const getRoleName = (roleId: number): string => {
        return roleIdToNameMap.get(roleId) || `Role ${roleId}`;
    };

    const renderAccessDetails = (access: IAccess, index: number) => {
        return (
            <Panel
                header={
                    <div className="d-flex justify-content-between align-items-center">
                        <span>
                            <strong>Role:</strong> {titleCase(getRoleName(access.role_id))}
                        </span>
                        <Tag color="blue">
                            {access.clients?.length || 0} Client{access.clients?.length !== 1 ? 's' : ''}
                        </Tag>
                    </div>
                }
                key={index}
            >
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <div className="card">
                            <div className="card-header">
                                <h6 className="mb-0">Role Information</h6>
                            </div>
                            <div className="card-body">
                                <Row gutter={[24, 8]}>
                                    <Col span={8}>
                                        <div className="info-field">
                                            <div className="label">Role ID</div>
                                            <div className="value">{access.role_id}</div>
                                        </div>
                                    </Col>
                                    <Col span={8}>
                                        <div className="info-field">
                                            <div className="label">Role Name</div>
                                            <div className="value">{getRoleName(access.role_id) || "-"}</div>
                                        </div>
                                    </Col>
                                    <Col span={8}>
                                        <div className="info-field">
                                            <div className="label">Role Slug</div>
                                            <div className="value">{access.role_slug || "-"}</div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        </div>
                    </Col>

                    <Col span={24}>
                        <div className="card">
                            <div className="card-header">
                                <h6 className="mb-0">Platform Access</h6>
                            </div>
                            <div className="card-body">
                                <div className="d-flex flex-wrap gap-2">
                                    {access.platforms && access.platforms.length > 0 ? (
                                        access.platforms.map((platform, idx) => (
                                            <Tag key={idx} color="green">
                                                {titleCase(platform)}
                                            </Tag>
                                        ))
                                    ) : (
                                        <span className="text-muted">No platform access</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Col>

                    <Col span={24}>
                        <div className="card">
                            <div className="card-header">
                                <h6 className="mb-0">Client Access</h6>
                            </div>
                            <div className="card-body">
                                {access.clients && access.clients.length > 0 ? (
                                    <Table
                                        columns={accessColumns}
                                        dataSource={access.clients}
                                        rowKey="client_id"
                                        pagination={false}
                                        size="small"
                                        className="access-table"
                                    />
                                ) : (
                                    <div className="text-muted text-center py-3">
                                        No client access configured
                                    </div>
                                )}
                            </div>
                        </div>
                    </Col>

                    {access.channels && access.channels.length > 0 && (
                        <Col span={24}>
                            <div className="card">
                                <div className="card-header">
                                    <h6 className="mb-0">Channel Access</h6>
                                </div>
                                <div className="card-body">
                                    <div className="d-flex flex-wrap gap-3">
                                        {access.channels.map((channel, idx) => (
                                            <div key={idx} className="channel-item d-flex align-items-center p-2">
                                                {channel.logo_url && (
                                                    <div className="me-2 channel-logo-container">
                                                        <img
                                                            src={channel.logo_url}
                                                            alt={titleCase(channel.name)}
                                                            className="channel-logo"
                                                        />
                                                    </div>
                                                )}
                                                <span className="fw-medium">
                                                    {titleCase(channel.name)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </Col>
                    )}
                </Row>
            </Panel>
        );
    };

    return (
        <div className="client-details">
            <div className="card">
                <div className="card-header">
                    <h5 className="mb-0">Access Overview</h5>
                </div>
                <div className="card-body">
                    <Row gutter={[24, 16]}>
                        <Col span={8}>
                            <div className="info-field text-center">
                                <div className="value fs-4 fw-bold text-primary">
                                    {user.access?.length || 0}
                                </div>
                                <div className="label">Access Configurations</div>
                            </div>
                        </Col>
                        <Col span={8}>
                            <div className="info-field text-center">
                                <div className="value fs-4 fw-bold text-success">
                                    {user.access?.reduce((total, access) => total + (access.clients?.length || 0), 0) || 0}
                                </div>
                                <div className="label">Total Client Access</div>
                            </div>
                        </Col>
                        <Col span={8}>
                            <div className="info-field text-center">
                                <div className="value fs-4 fw-bold text-info">
                                    {user.is_super_admin ? "Super Admin" : "Regular User"}
                                </div>
                                <div className="label">User Type</div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            <div className="card">
                <div className="card-header">
                    <h5 className="mb-0">Detailed Access Configuration</h5>
                </div>
                <div className="card-body">
                    {user.access && user.access.length > 0 ? (
                        <Collapse className="access-collapse">
                            {user.access.map((access, index) => renderAccessDetails(access, index))}
                        </Collapse>
                    ) : (
                        <div className="text-center py-5">
                            <div className="text-muted">
                                <div className="fs-5 mb-2">No Access Configuration</div>
                                <div>This user has no access configurations set up.</div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserAccess;